import React from "react";
import styled from "styled-components";
import { get, head } from "lodash";
import { ChevronDown, CircleX } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { Button, ButtonProps } from "@sonder/ui/source/styled/fragments/Button";
import { ButtonList } from "@sonder/ui/source/styled/fragments/ButtonList";
import { CancelButton } from "@sonder/ui/source/styled/fragments/CancelButton";
import { RadioDropdownItem } from "@sonder/ui/source/styled/fragments/RadioDropdownItem";
import { SvgIconWrapper } from "@sonder/ui/source/styled/fragments/SvgIconWrapper";
import { DropdownContentAlignment } from "@sonder/ui/source/styled/surfaces/DropdownContent";
import { colors, sizes } from "@sonder/ui/source/styled/theme";
import {
  TagSubtypes,
  TagTypes,
  TagsToIcons,
  TomTags,
  useGetTagsQuery,
} from "core/queries/GetTags";

const StyledIconWrapper = styled(SvgIconWrapper)`
  margin-right: 0.75rem;
`;

const StyledDropdownContent = styled(DropdownContent)`
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  justify-content: flex-start;
`;

const StyledIcon = styled.span`
  display: inline-flex;
  margin: 0 0.5rem;
  flex: 0 0 ${sizes.iconDefault};
`;

interface StyledTriggerProps extends ButtonProps {
  isUnset: boolean;
}
const StyledTrigger = styled(StyledButton)<StyledTriggerProps>`
  border: 1px solid transparent;

  &:focus:not(:disabled),
  &:hover:not(:disabled) {
    border: 1px solid ${props => (props.selected ? colors.blue7 : colors.grayE)};
  }

  &:not(:focus):after {
    bottom: 0.25rem;
    border-top: 1px dashed ${colors.grayE};
    content: "";
    display: ${props =>
      props.selected || !props.isUnset ? "none" : "inherit"};
    left: 0.5rem;
    position: absolute;
    right: 0.5rem;
  }

  ${CancelButton} {
    border-radius: 50%;
    display: none;
    margin: 0 0 0 0.7rem;
    padding: 0.1rem;

    > svg {
      height: 1.3rem;
      width: 1.3rem;
    }
  }

  &:hover:not(:disabled) {
    ${CancelButton} {
      display: inline-flex;
    }

    &:after {
      border-color: ${colors.black};
    }
  }
`;

const StyledButtonList = styled(ButtonList)`
  max-height: 16.5rem;
  min-width: 12rem;
  padding-bottom: 0.5rem;
  max-width: 22rem;
  width: 100%;
`;

const StyledEmpty = styled.p`
  color: ${colors.grayA};
  text-align: center;
`;

interface RenderTriggerProps {
  disabled?: boolean;
  onClear?(): void;
  selectedTag: TomTags | undefined;
}

interface SquadPickerProps {
  alignContent?: DropdownContentAlignment;
  disabled?: boolean;
  onChange?(newTags: TomTags[]): void;
  renderTrigger?: (args: RenderTriggerProps) => JSX.Element;
  tags: TomTags[];
  tagType?: string;
}

export const SquadPickerTrigger = ({
  disabled,
  onClear,
  selectedTag,
}: RenderTriggerProps) => {
  const SelectedTagIcon = TagsToIcons(
    (selectedTag && selectedTag.name) || TagSubtypes.Unset,
  );
  const selectedTagName = (selectedTag && selectedTag.name) || null;
  return (
    <StyledTrigger
      iconLeft
      iconRight
      disabled={disabled}
      isUnset={!!selectedTagName}
      type="button"
    >
      <StyledIcon>{SelectedTagIcon && <SelectedTagIcon />}</StyledIcon>
      {selectedTagName && <span>{selectedTagName}</span>}
      {!!selectedTag && (
        <CancelButton
          onClick={e => {
            if (onClear) {
              onClear();
            }
            e.stopPropagation();
          }}
        >
          <CircleX />
        </CancelButton>
      )}
      <ChevronDown />
    </StyledTrigger>
  );
};

export const SquadPicker = ({
  alignContent,
  disabled = false,
  onChange = () => undefined,
  renderTrigger = SquadPickerTrigger,
  tags = [],
  tagType = TagTypes.Squad,
}: SquadPickerProps) => {
  // TODO: Support Multiple Tags at some point.
  const selectedTag = head(tags);
  const { data, loading } = useGetTagsQuery({
    variables: {
      tagType,
    },
  });
  const squads = get(data, "tomTags") || [];
  const isEmpty = squads.length === 0;
  const onClear = () => {
    onChange([]);
  };
  return (
    <Dropdown
      trigger={renderTrigger({
        disabled,
        onClear,
        selectedTag,
      })}
    >
      {({ closePortal, isOpen: isPortalOpen }) => {
        const squadButtons = squads.map(squad => {
          const TagIcon = TagsToIcons(squad.name);
          const isSelected = selectedTag && squad.id === selectedTag.id;
          return (
            <RadioDropdownItem
              checked={isSelected}
              data-testid="squad-picker-dropdown-item"
              key={`${tagType}|${squad.id}`}
              onClick={() => {
                closePortal();
                if (isSelected) {
                  onClear();
                  return;
                }
                onChange([squad]);
              }}
            >
              {TagIcon && (
                <StyledIconWrapper>
                  <TagIcon />
                </StyledIconWrapper>
              )}
              {squad.name}
            </RadioDropdownItem>
          );
        });
        return (
          isPortalOpen && (
            <StyledDropdownContent align={alignContent}>
              {isEmpty && (
                <StyledEmpty>
                  {!loading ? "No Squads available." : "Loading..."}
                </StyledEmpty>
              )}
              <StyledButtonList>{squadButtons}</StyledButtonList>
            </StyledDropdownContent>
          )
        );
      }}
    </Dropdown>
  );
};
